import { useMemo } from 'react';
import { css } from '@emotion/react';
import { DashboardWidget, DashboardWidgetFC, TimePeriodListBoxMenu } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { useCallIntelDashWidgetVisibility } from '../../hooks/useCallIntelDashWidgetVisibility';
import { useCallIntelStatsComparisonData } from '../../hooks/useCallIntelStatsComparisonData';
import { ScheduleOpportunityChart } from './ScheduleOpportunityChart';
import { ScheduleOpportunityStats } from './ScheduleOpportunityStats';

/**
 * @dashboard-widget
 *
 * id: call-intel-scheduling-opportunities-widget
 * title: Call Intelligence - Scheduling Opportunities
 * description: Stay on top of how many calls led to scheduled appointments, how many calls had missed scheduling opportunities, and your overall scheduling rate.
 * icon: call-intel-small
 */
export const CallIntelScheduleOpportunityWidget: DashboardWidgetFC = () => {
  const { t } = useTranslation('callIntelDashboardWidgets');

  const { locationIdsWithPermission } = useCallIntelDashWidgetVisibility({
    widgetId: 'call-intel-scheduling-opportunities-widget',
  });

  const { currentStats, previousStats } = useCallIntelStatsComparisonData({
    locationIds: locationIdsWithPermission,
    comparisonPeriod: 'week',
    statsIncludes: { opportunities: true, serviceQuality: false },
    fetchOverviewDetails: false,
  });

  const { scheduledCount, scheduledRate, unscheduledCount, rateChange, unscheduledRate, totalCount } = useMemo(() => {
    const scheduledCount = currentStats?.summary?.scheduledOpportunities ?? 0;
    const totalCount = currentStats?.summary?.totalOpportunities ?? 0;
    const unscheduledCount = totalCount - scheduledCount;
    const scheduledRate = Math.round(totalCount > 0 ? (scheduledCount / totalCount) * 100 : 0);
    const unscheduledRate = Math.round(totalCount > 0 ? (unscheduledCount / totalCount) * 100 : 0);

    const previousScheduledCount = previousStats?.summary?.scheduledOpportunities ?? 0;
    const previousTotalCount = previousStats?.summary?.totalOpportunities ?? 0;
    const previousScheduledRate = Math.round(
      previousTotalCount > 0 ? (previousScheduledCount / previousTotalCount) * 100 : 0
    );

    return {
      scheduledCount,
      unscheduledCount,
      scheduledRate,
      unscheduledRate,
      rateChange: scheduledRate - previousScheduledRate,
      totalCount,
    };
  }, [currentStats, previousStats]);

  return (
    <DashboardWidget css={{ padding: theme.spacing(1.25, 2) }}>
      <DashboardWidget.Header icon='call-intel-small' title={t('Call Intelligence: Scheduling Opportunities')}>
        <div css={{ flexShrink: 0 }}>
          <TimePeriodListBoxMenu value='week' readonly />
        </div>
      </DashboardWidget.Header>
      <DashboardWidget.Content css={styles.container}>
        <div css={styles.contentLayout}>
          <ScheduleOpportunityStats
            scheduledCount={scheduledCount}
            scheduledRate={scheduledRate}
            unscheduledCount={unscheduledCount}
            rateChange={rateChange}
          />
          <ScheduleOpportunityChart
            scheduledCount={scheduledCount}
            scheduledRate={scheduledRate}
            unscheduledCount={unscheduledCount}
            unscheduledRate={unscheduledRate}
            totalOpportunities={totalCount}
            totalLocations={locationIdsWithPermission.length}
          />
        </div>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

CallIntelScheduleOpportunityWidget.config = {
  size: {
    large: 'small-wide',
    medium: 'small-wide',
    small: 'medium-narrow',
    extraSmall: 'medium-narrow',
  },
  feature: 'analytics',
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    container-type: inline-size;
    flex: 1;
    overflow: hidden;
  `,
  contentLayout: css`
    display: flex;
    gap: ${theme.spacing(4)};
    flex: 1;
    @container (max-width: ${breakpoints.small.min}px) {
      flex-direction: column;
      gap: ${theme.spacing(1)};
      justify-content: space-evenly;
    }
  `,
};
